/**
 * BoxOfficeProjectionsContainer Component
 * Main container for box office projections with trend visualization and admin management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  Assessment,
  Timeline
} from '@mui/icons-material';
import { useBoxOfficeProjections } from '../../hooks/useBoxOfficeProjections';
import ProjectionsChart from './ProjectionsChart';
import ProjectionsList from './ProjectionsList';
import ProjectionsSummary from './ProjectionsSummary';

const BoxOfficeProjectionsContainer = ({ 
  filmId, 
  filmTitle 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  
  // Use box office projections hook
  const {
    projections,
    loading,
    error,
    fetchFilmProjections,
    refreshProjections,
    getLatestProjection,
    getProjectionTrend,
    hasProjections,
    isEmpty
  } = useBoxOfficeProjections(filmId);

  // Fetch projections on mount
  useEffect(() => {
    if (filmId) {
      fetchFilmProjections(filmId);
    }
  }, [filmId, fetchFilmProjections]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle refresh
  const handleRefresh = async () => {
    await refreshProjections();
  };

  // Get latest projection for summary
  const latestProjection = getLatestProjection();
  const trend = getProjectionTrend();

  const canManageProjections = false; // Read-only in Film Detail pages

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Box Office Projections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Opening weekend box office projections and trend analysis for <strong>{filmTitle}</strong>
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<Assessment />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Section */}
      {hasProjections && (
        <Box sx={{ mb: 3 }}>
          <ProjectionsSummary
            projections={projections}
            latestProjection={latestProjection}
            trend={trend}
            filmTitle={filmTitle}
          />
        </Box>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ p: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab
            label="Trend Analysis"
            icon={<Timeline />}
            iconPosition="start"
          />
          <Tab
            label="Projections List"
            icon={<Assessment />}
            iconPosition="start"
          />
        </Tabs>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : isEmpty ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Projections Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No box office projections have been created for this film yet.
                </Typography>
              </Box>
            ) : (
              <ProjectionsChart
                projections={projections}
                filmTitle={filmTitle}
                loading={loading}
                error={error}
              />
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : isEmpty ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Projections Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No box office projections have been created for this film yet.
                </Typography>
              </Box>
            ) : (
              <ProjectionsList
                projections={projections}
                filmId={filmId}
                filmTitle={filmTitle}
                canManage={false}
                onRefresh={handleRefresh}
                onEditProjection={null}
                loading={loading}
                error={error}
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BoxOfficeProjectionsContainer;
